import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Observable, of, Subscription} from 'rxjs';
import {PersonRestService} from '../../shared/services/person/person-rest.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {Person} from '../../shared/types/person/person';
import {GlobalMessagesService} from '../../shared/services/messages/global.messages.service';
import {Location} from '@angular/common';
import {ConfirmationService} from 'primeng/api';
import {FileRestService} from '../../shared/services/common/file/file-rest.service';
import {FileUtilsService} from '../../shared/services/common/file/file-utils.service';
import {UntypedFormGroup} from '@angular/forms';
import {FormHelperService} from '../../shared/services/person/form-helper-service';
import {ContactType} from '../../shared/types/contact/contact-type';
import {TranslateService} from '@ngx-translate/core';
import {UserRole} from '../../shared/types/userrole/user-role';
import {SecurityService} from '../../shared/services/security/security.service';
import {SystemType} from '../../shared/types/common/system-type';
import {Region} from '../../shared/types/organization/region';
import {RegionRestService} from '../../shared/services/region/region-rest.service';
import {map, mergeMap} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {User} from '../../shared/types/security/user';
import {FileUpload} from 'primeng/fileupload';

@Component({
  selector: 'wsd-person-modify',
  templateUrl: './person-modify.component.html',
  styleUrls: ['./person-modify.component.css']
})
export class PersonModifyComponent implements OnInit, OnDestroy {

  person: Person = {
    appointments: [],
    contacts: [],
    expertiseAreas: [],
    languages: [],
    addresses: []
  } as Person;

  @ViewChild('fileUpload')
  fileUpload: FileUpload;

  idmAccountUrl: string = environment.idmAccountUrl;

  loaded: boolean = false;

  confirmedNonAppointment = false;

  parentForm: UntypedFormGroup;

  imageSrc: any;

  private imageFile: File;

  private routeSub: Subscription;

  deletedImage: boolean;

  countryId: number = null;

  isSaveDisabled: boolean = false;

  userRole: UserRole;

  hasWorldGroupAdmin: boolean = false;

  hasUpdateRegionPermission: boolean = false;

  isLoggedUser: boolean;

  personBeforeSave: Person;

  languageFilters = {
    global: ['English', 'French'],
    working: ['Arabic', 'English', 'French', 'Russian', 'Spanish']
  };

  showSynchronizationInfoMessage: boolean = false;

  constructor(private securityService: SecurityService,
              private personRestService: PersonRestService,
              private globalMessageService: GlobalMessagesService,
              private formHelper: FormHelperService,
              private fileRestService: FileRestService,
              private translate: TranslateService,
              private fileUtilsService: FileUtilsService,
              private router: Router,
              private confirmService: ConfirmationService,
              private location: Location,
              private route: ActivatedRoute,
              private regionService: RegionRestService) {
  }

  ngOnInit() {
    this.parentForm = this.formHelper.buildPersonForm();
    this.routeSub = this.route.params.subscribe(
      (params: Params) => this.loadPerson(params)
    );
    this.securityService.hasPermission('REGION_PERSON_UPDATE').subscribe(hasUpdatePermission => {
      this.hasUpdateRegionPermission = hasUpdatePermission;
      if (!hasUpdatePermission) {
        this.parentForm.get('region').setValidators([]);
      }
    });
  }

  private loadPerson(params: Params) {
    this.loaded = false;

    if (params['id']) {
      this.personRestService.getPerson(+params['id']).subscribe(
        (person: Person) => {
          this.initForm(person);
          if (person.keyCloakId) {
            this.parentForm.get('workingLanguage').setValidators([]);
            this.parentForm.get('region').setValidators([]);
          }
          this.securityService.getLoggedUser().subscribe(
            (user: User) => this.isLoggedUser = (this.person.id === user.consultantId)
          );
        }
      );
    } else {
      this.initForm(this.person);
    }
  }

  private initForm(data: Person) {
    this.person = data;
    if (this.person.country) {
      this.countryId = this.person.country.id;
    }
    this.checkIfHasWorldGroupAdminRole();
    this.formHelper.initPersonForm(this.parentForm, this.person);

    if (this.person.profileImageId) {
      this.loadImage(this.person.profileImageId);
    }

    this.personBeforeSave = <Person>JSON.parse(JSON.stringify(data));
    this.loaded = true;
  }

  checkIfHasWorldGroupAdminRole() {
    if (this.person.roles) {
      this.person.roles.forEach(
        entry => {
          if (entry.id === 14) {
            this.hasWorldGroupAdmin = true;
            this.userRole = entry;
            // this.person.roles.splice(this.person.roles.indexOf(entry), 1);
          }
        }
      );
    }
  }

  onSelect(event) {
    this.imageSrc = event.files[0].objectURL;
    this.imageFile = event.files[0];
    this.fileUpload.clear();
  }

  private loadImage(imgId: number) {
    if (!imgId) {
      return;
    }
    this.fileRestService.getImage(imgId.toString()).subscribe(
      ((data: any) => {
        this.imageSrc = this.fileUtilsService.getImage(data.content);
        this.deletedImage = false;
      })
    );
    console.log(this.person);
  }

  async save() {

    if (this.canSave()) {
      this.isSaveDisabled = true;
      if (this.person.id && this.person.appointments.length === 0) {
        this.confirmService.confirm({
          header: this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.DELETE_CONFIRM_HEADER'),
          message: this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.DELETE_CONFIRM_MESSAGE'),
          accept: () => {
            this.performSave();
            this.router.navigate(['/contacts']);
            this.confirmedNonAppointment = true;
          }, reject: () => {
            this.isSaveDisabled = false;
          }
        });
      } else if ((!this.person.id) || (await this.isPersonalDataChanged() && this.person.consent === false)) {
        this.confirmService.confirm({
          header: this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.SAVE_CONSENT_HEADER'),
          message: this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.SAVE_CONSENT_MESSAGE'),
          accept: () => {
            this.person.consent = true;
            this.person.consentGivenDate = new Date();
            this.performSave();
          }, reject: () => {
            this.isSaveDisabled = false;
          }
        });
      } else {
        this.performSave();
      }
    }
  }

  private performSave() {
    this.prepareData().subscribe(preparedData => {
      this.personRestService.save(preparedData, this.imageFile).subscribe(
        (savedPerson) => this.afterSave(savedPerson),
        () => {
          this.globalMessageService.showErrorMessage(this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.SAVE_ERROR_MESSAGE'));
          this.isSaveDisabled = false;
        },
        () => {
          this.isSaveDisabled = false;
        });
    });
  }

  private canSave() {
    if (!this.parentForm.valid) {
      this.globalMessageService.showErrorMessage(this.formHelper.getFormValidationErrors(this.parentForm),
        this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.VALIDATION_ERRORS'));
      this.formHelper.validateAllFormFields(this.parentForm);
      return false;
    }

    if (!this.person.id && this.person.appointments.length === 0) {
      this.globalMessageService.showErrorMessage(this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.NO_APPOINTMENTS_MESSAGE'));
      return false;
    }
    return true;
  }

  private afterSave(savedPerson) {
    this.checkIfHasWorldGroupAdminRole();
    this.person.contacts = savedPerson.contacts;
    this.person.addresses = savedPerson.addresses;
    this.setCountryId(savedPerson.country);
    if (!this.person.id) {
      this.router.navigate(['/persons', savedPerson.id, 'detail']);
    } else {
      if (!this.confirmedNonAppointment) {
        this.router.navigate(['/persons', savedPerson.id, 'detail']).then(() => {
          this.globalMessageService.showSuccessMessage(this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.SAVE_MESSAGE'));
        });
      } else {
        this.globalMessageService.showSuccessMessage(this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.SAVE_MESSAGE'));
      }
      //this.globalMessageService.showSuccessMessage(this.translate.instant('WS_DIRECTORY.PERSON_MODIFY.SAVE_MESSAGE'));
    }

  }

  private async isPersonalDataChanged(): Promise<boolean> {

    return await new Promise((resolve) => {
      this.prepareData().subscribe(personAfterSave => {

        if ((this.personBeforeSave.firstName !== personAfterSave.firstName)
          || (this.personBeforeSave.lastName !== personAfterSave.lastName)
          || (this.personBeforeSave.middleName !== personAfterSave.middleName)
          || (this.personBeforeSave.profileImageURL !== personAfterSave.profileImageURL)
          || (JSON.stringify(this.personBeforeSave.contacts) !== JSON.stringify(personAfterSave.contacts))) {
          this.person.consent = false;
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });


  }

  private prepareData(): Observable<any> {
    const self = this;
    self.parentForm.value.removeImage = this.deletedImage;
    this.hasWorldGroupAdmin = false;
    if (!this.hasUpdateRegionPermission) {
      return this.securityService.getLoggedUser().pipe(mergeMap(user => {
        return this.regionService.getAllRegions(SystemType.DIRECTORY).pipe(map(
          (regions: Region[]) => {
            let regionId = 0;
            regions.forEach(region => {
              if (region.name === user.organization.region) {
                regionId = region.id;
              }
            });
            self.parentForm.get('region').setValue({
              id: regionId,
              name: user.organization.region
            });
            this.parentForm.get('organization').setValue(user.organization);
            return {...self.person, ...self.parentForm.value};
          }
        ));
      }));
    } else {
      return of({...self.person, ...self.parentForm.value});
    }
  }

  discard() {
    this.location.back();
  }

  getFullName() {
    return [
      this.person.title,
      this.person.firstName,
      this.person.middleName,
      this.person.lastName,
      this.person.suffix,
      this.person.honours
    ].filter(Boolean).join(' ');
  }

  getEmails() {
    return this.person.contacts && this.person.contacts.filter(
      value => value.type === ContactType.EMAIL
    );
  }

  setCountryId(country: any) {
    if (country !== null) {
      this.countryId = country.id;
    }
  }

  removeImage() {
    this.imageSrc = null;
    this.imageFile = null;
    this.deletedImage = true;
    this.fileUpload.clear();
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  stringifyRoles(roles: UserRole[]) {
    let rolesString = '';
    if (roles.length > 0) {
      roles.forEach(role => rolesString += role.name + ', ');
      return rolesString.substring(0, rolesString.length - 2);
    } else {
      return '';
    }
  }
}
